import { Sprite, Text, type Texture } from 'pixi.js';
import { BoardDialog } from '../board/BoardDialog';
import { BrassButton, layoutButtonRow } from '../board/BrassButton';
import { COLOURS, displayStyle, uiStyle } from '../board/ui';
import { HOLD_AND_SPIN_NAME } from './HoldAndSpinBoard';

/** How long the board waits for COLLECT before collecting by itself. */
const AUTO_COLLECT_MS = 8000;

/**
 * The end of the hold-and-spin bonus, on a board (producer 2026-10-14: no
 * plain dialogs): what the locked coins paid, how many there were, and
 * COLLECT, which sends the coins flying into the wallet. It collects by
 * itself after a while so the slot never waits on a player who has
 * looked away.
 */
export class BonusWinDialog extends BoardDialog {
  private readonly coin = new Sprite();
  private readonly amount: Text;
  private readonly line: Text;
  private readonly collect: BrassButton;
  private done: (() => void) | null = null;
  private timer: ReturnType<typeof setTimeout> | null = null;

  constructor(private readonly art: { floor: () => Texture | null; coin: () => Texture | null }) {
    super({ title: HOLD_AND_SPIN_NAME.toUpperCase(), floor: art.floor(), titleSize: 18 });
    this.coin.anchor.set(0.5);
    this.amount = new Text({ text: '', style: displayStyle(44) });
    this.amount.anchor.set(0, 0.5);
    this.line = new Text({ text: '', style: { ...uiStyle(16, COLOURS.cream), align: 'center', wordWrap: true } });
    this.line.anchor.set(0.5, 0);
    this.collect = new BrassButton({ label: 'COLLECT', kind: 'gold', onTap: () => this.finish() });
    this.content.addChild(this.coin, this.amount, this.line, this.collect);
  }

  /** The board as plain data, for the browser tests. */
  get snapshot(): { open: boolean; title: string; amount: string; line: string; buttons: string[] } {
    return {
      open: this.visible,
      title: this.titleText,
      amount: this.amount.text,
      line: this.line.text,
      buttons: this.visible ? [this.collect.labelText] : [],
    };
  }

  /** Press a button by its label (browser tests). */
  press(label: string): boolean {
    if (!this.visible || this.collect.labelText !== label) return false;
    this.finish();
    return true;
  }

  /**
   * Show what the bonus paid; resolves when the player collects (or it
   * collects by itself).
   */
  async play(result: { total: number; locked: number; fullGrid: boolean }): Promise<void> {
    this.setTitle(result.fullGrid ? 'GRAND JACKPOT!' : HOLD_AND_SPIN_NAME.toUpperCase());
    this.setFloor(this.art.floor());
    const coin = this.art.coin();
    if (coin) this.coin.texture = coin;
    this.coin.visible = coin !== null;
    this.amount.text = `+${result.total.toLocaleString('en-GB')}`;
    this.line.text = result.fullGrid
      ? `Every cell filled with coins. All ${result.locked} pay.`
      : `${result.locked} locked coin${result.locked === 1 ? '' : 's'}, all yours.`;
    this.arrange();
    const closed = new Promise<void>((resolve) => (this.done = resolve));
    await this.open();
    void this.celebrate({ x: this.contentWidth / 2, y: 30 });
    this.timer = setTimeout(() => this.finish(), AUTO_COLLECT_MS);
    return closed;
  }

  layout(viewWidth: number, viewHeight: number): void {
    super.layout(viewWidth, viewHeight);
    this.arrange();
  }

  private arrange(): void {
    if (!this.amount) return;
    const w = this.contentWidth;
    const coinSize = 44;
    this.coin.width = this.coin.height = coinSize;
    const rowW = (this.coin.visible ? coinSize + 8 : 0) + this.amount.width;
    const x0 = (w - rowW) / 2;
    const rowY = 30;
    this.coin.position.set(x0 + coinSize / 2, rowY);
    this.amount.position.set(x0 + (this.coin.visible ? coinSize + 8 : 0), rowY);
    this.line.style.wordWrapWidth = w - 8;
    this.line.position.set(w / 2, rowY + 32);
    const buttonY = this.line.y + this.line.height + 16;
    layoutButtonRow(null, this.collect, 0, buttonY, w);
    this.setContentHeightQuiet(buttonY + this.collect.buttonHeight + 4);
  }

  private finish(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    const done = this.done;
    this.done = null;
    if (!done) return;
    void this.close().then(done);
  }

  destroy(options?: Parameters<BoardDialog['destroy']>[0]): void {
    // Leaving mid-bonus: the auto-collect must not fire on a dead board.
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.done = null;
    super.destroy(options);
  }

  /** Board height without re-entering {@link arrange} through layout. */
  private setContentHeightQuiet(height: number): void {
    if (this.arranging) return;
    this.arranging = true;
    this.setContentHeight(height);
    this.arranging = false;
  }

  private arranging = false;
}
